/**
 * Utility functions for PO Lines Tab
 */
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { ACKNOWLEDGE_STATUS_CONFIG, StatusConfig } from './types';
import type { User } from '../../../../lib/api';

/**
 * Get sort icon based on current sort state
 */
export function getSortIcon(
  field: string,
  sortField: string | null,
  sortDirection: 'asc' | 'desc'
) {
  if (sortField !== field) {
    return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />;
  }
  return sortDirection === 'asc' ? (
    <ArrowUp className="h-3 w-3 text-primary" />
  ) : (
    <ArrowDown className="h-3 w-3 text-primary" />
  );
}

/**
 * Get acknowledge status badge element
 */
export function getAcknowledgeStatusBadge(status: string) {
  const config: StatusConfig = ACKNOWLEDGE_STATUS_CONFIG[status] || {
    label: status || 'Unknown',
    className: 'bg-gray-100 text-gray-800'
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

/**
 * Check if user can release a hold on a line.
 * Admins and staff can always release holds.
 * Customers and suppliers can release holds they placed.
 * Falls back to true if no user is provided (backwards compatibility).
 */
export function canReleaseHold(user?: User | null): boolean {
  if (!user) return true;

  // Staff and superusers can always release holds
  if (user.is_staff || user.is_superuser) return true;

  // Users with admin or customer platform roles can release
  if (user.platform === 'CUSTOMER' || user.platform === 'HELIX') return true;

  // Suppliers can release holds they are allowed to
  if (user.platform === 'SUPPLIER') return true;

  // Default: allow release (graceful fallback)
  return true;
}
